import Cookies from 'js-cookie'
import { defineStore } from 'pinia'
import { z } from 'zod'
import { useLogin, type UseInfiniteLoginType } from '@/composables/use-login'
import type { OtpLoginToken } from '@/models/api/otp-login-token'
import { userProfileSchema, type UserProfile } from '@/models/api/user-profile'
import { CookiesKey } from '@library/enums/cookies-key'
import { SessionStorageKey } from '@library/enums/session-storage-key'
import { useStorage } from '~library/composables'
import { Identity, LocalStorage } from '~library/enums'
import { isType } from '~library/utils/functions'

const optDataSchema = z.object({
  account: z.string(),
  email: z.string(),
  otpRemainingTime: z.number(),
  sseCode: z.string(),
  identity: z.nativeEnum(Identity),
  userProfile: userProfileSchema
})

type OtpData = z.infer<typeof optDataSchema>

export const useOtpStore = defineStore('otp', () => {
  const { setDataAfterLogin } = useLogin()
  const { setStorageData, removeStorageData, getStorageData } = useStorage()

  const navigationType = ref<PerformanceNavigationTiming['type'] | null>(
    (
      window.performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming
    ).type
  )

  const _otpData = ref<OtpData | null>(null)
  const _otpLoginToken = ref<OtpLoginToken | null>(null)
  const _otpIsExpired = ref<boolean>(false)
  const _otpIsReload = ref<boolean>(false)

  /** otp 剩餘時間 */
  const otpRemainingTime = computed(
    (): number | undefined => _otpData.value?.otpRemainingTime
  )

  /** otp data */
  const otpData = computed((): OtpData | null => _otpData.value)

  /** otp otpLoginToken */
  const otpLoginToken = computed(
    (): OtpLoginToken | null => _otpLoginToken.value
  )

  /** otp 驗證碼是否過期 */
  const otpIsExpired = computed((): boolean => _otpIsExpired.value)

  /** otp 頁面是否重新整理 */
  const otpIsReload = computed((): boolean => _otpIsReload.value)

  /** 設定 otp data */
  const setOtpData = (
    userProfile: UserProfile,
    loginData: UseInfiniteLoginType
  ) => {
    _otpData.value = {
      account: userProfile.memberCardNumber,
      email: userProfile.email,
      otpRemainingTime: userProfile.otpRemainingTime,
      sseCode: userProfile.sseCode,
      identity: loginData.loginData.identity,
      userProfile: userProfile
    }

    setOtpIsReload(false)
    setOtpLastStep(false)
    setOtpStorage()
  }

  /** 清除 otp data */
  const clearOtpData = () => {
    _otpData.value = null
    setOtpIsReload(false)
    clearOtpStorage()
  }

  /** 重拿 otp 倒數時間  */
  const setOtpRemainingTime = (data: OtpData) => {
    _otpData.value = data
    setOtpIsReload(false)
    setOtpStorage()
  }

  /** otp 驗證碼過期 */
  const otpDataExpired = () => {
    _otpIsExpired.value = true
    setOtpIsExpired(true)
    clearOtpData()
  }

  /** otp 初始化驗證碼過期狀態 */
  const otpDataUnExpired = () => {
    _otpIsExpired.value = false
    setOtpIsExpired(false)
  }

  /** otp 存Storage */
  const setOtpStorage = () => {
    Cookies.set(CookiesKey.OTP_DATA, JSON.stringify(otpData.value))
    setStorageData(LocalStorage.OTP_DATA, Date.now())
    sessionStorage.setItem(
      SessionStorageKey.OTP_DATA,
      JSON.stringify(otpData.value)
    )
  }

  /** otp 刪除Storage */
  const clearOtpStorage = () => {
    Cookies.remove(CookiesKey.OTP_DATA)
    removeStorageData(LocalStorage.OTP_DATA)
    sessionStorage.removeItem(SessionStorageKey.OTP_DATA)
  }

  /** otp 取得Storage */
  const getSessionOtpData = () => {
    setOtpLastStep(false)
    if (navigationType.value === 'reload') {
      const otpDataFromSessionStorage = JSON.parse(
        sessionStorage.getItem(SessionStorageKey.OTP_DATA) ?? 'null'
      )

      if (isType(otpDataFromSessionStorage, optDataSchema)) {
        _otpData.value = otpDataFromSessionStorage
      }

      setOtpStorage()
      setOtpIsReload(true)
      navigationType.value = null
    } else {
      const otpDataFromCookie = Cookies.get(CookiesKey.OTP_DATA)
      if (
        otpDataFromCookie &&
        isType(JSON.parse(otpDataFromCookie), optDataSchema)
      ) {
        _otpData.value = JSON.parse(otpDataFromCookie)
        sessionStorage.setItem(SessionStorageKey.OTP_DATA, otpDataFromCookie)
      }
    }
  }

  /** 設定 otp data */
  const setOtpIsReload = (value: boolean) => {
    _otpIsReload.value = value
  }

  /** otp 驗證完成 登入 */
  const setOtpLoginToken = (data: OtpLoginToken) => {
    _otpLoginToken.value = data
    Cookies.set(CookiesKey.DEVICE_TOKEN, data.deviceToken)
  }

  /** otp 驗證完成 登入 */
  const otpVerifySuccess = () => {
    if (otpData.value) {
      setDataAfterLogin({
        account: otpData.value.account,
        identity: otpData.value.identity,
        userProfile: { ...otpData.value.userProfile, ...otpLoginToken.value },
        setLang: true
      })
    }
    defaultOtpData()
  }

  /** otp 同步分頁資料 */
  const asyncOptData = () => {
    const otpDataFromCookie = Cookies.get(CookiesKey.OTP_DATA)
    if (
      otpDataFromCookie &&
      isType(JSON.parse(otpDataFromCookie), optDataSchema)
    ) {
      _otpData.value = JSON.parse(otpDataFromCookie)
      sessionStorage.setItem(SessionStorageKey.OTP_DATA, otpDataFromCookie)
      setOtpLastStep(false)
    }
  }

  /** otp 初始化所有資料 */
  const defaultOtpData = () => {
    clearOtpData()
    otpDataUnExpired()
    _otpLoginToken.value = null
    setOtpIsReload(false)
    navigationType.value = null
  }

  /** otp 初始化所有資料 但顯示已過期提示 */
  const defaultOtpDataWithExpired = () => {
    clearOtpData()
    otpDataExpired()
    setOtpIsReload(false)
    navigationType.value = null
  }

  /** 設定 otp 過期 */
  const setOtpIsExpired = (value: boolean) => {
    setStorageData(LocalStorage.OTP_IS_EXPIRED, value)
  }

  /** 設定 otp 上一步 */
  const setOtpLastStep = (value: boolean) => {
    setStorageData(LocalStorage.OTP_LAST_STEP, value)
  }

  /** otp 取得上一步參數 */
  const getOtpLastStep = () => {
    return getStorageData(LocalStorage.OTP_LAST_STEP)
  }

  return {
    otpData,
    otpLoginToken,
    otpRemainingTime,
    otpIsReload,
    otpIsExpired,
    getSessionOtpData,
    otpDataExpired,
    otpDataUnExpired,
    asyncOptData,
    setOtpRemainingTime,
    setOtpIsReload,
    otpVerifySuccess,
    setOtpData,
    setOtpLoginToken,
    defaultOtpData,
    defaultOtpDataWithExpired,
    setOtpLastStep,
    getOtpLastStep,
    setOtpIsExpired
  }
})
